/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import {store} from "./redux/store.jsx";
import {triggerError} from "./redux/actions.jsx";
import {Util} from "./Utilities.js";
import {Socket} from "./Socket.js";
import {WebWorker} from "./worker/Worker.js";
import CurrentUser from "authClient/currentUser";
import Fetch from "toolbox/fetch"
import Strings from 'toolbox/strings';
import {Notification} from "./Notification.jsx";
import Unique from "toolbox/unique";

const currentSubscriptions = []

const Database = function () {

    function table(tableName, scope = 'data', columns = []) {
        if (scope === false) {
            scope = 'data'
        } else if (scope === true) {
            scope = 'app'

        }


        /**
         * This allows to get a row by a value and a key
         * @param {string | 'indexKey'} key - The name of the column
         * @param {string} value - The Value to look for
         * @param {('is' | 'in' | 'isnot' | 'isexact')} [type='is'] - The type for the filter z.b is or isnot
         * @return {Promise<FetchResponse>}
         */
        function simpleFilter(key, value, type = "is", historyTimestamp = 0) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify([{
                column: key, tableName: tableName, type: type, value: value
            }]));

            urlencoded.append("scope", scope);
            urlencoded.append("historyTimestamp", historyTimestamp);


            return fetchProxy("erp/table/withfilter", urlencoded)

        }

        /**
         * Get a record by its indexKey value
         * @param {string} value - The value of the indexKey
         */
        function getByKey(value) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify([{
                column: 'indexKey', tableName: tableName, type: 'isexact', value: value
            }]));
            // check if the value is a uuid v4
            if (!(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value))) {
                store.dispatch(triggerError({
                    title: "GetByKey only gets a uuid as input ",
                    text: 'The input is "' + value + "' witch does not match the schema of a uuid v4",
                    linkShowMe: <div></div>,
                    errorSourcePath: "",
                }))

            }

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/withfilter", urlencoded)

        }

        /**
         * Gives back the table witch is filtered by the columns defined in {@link filterColumns}
         * @param {Object[]} filterColumns - The Filter Columns Array
         * @param {number} startIndex
         * @param {number} stopIndex
         * @param {string} filterColumns[].column - The name of the Column.
         * @param {string} filterColumns[].tableName - The Table Name of the column
         * @param {('is' | 'in'| 'notin' | 'isnot' | 'isexact')} filterColumns[].type - The type of the filter
         * @param {string} filterColumns[].value - The name of the Column.
         * @return {Promise<FetchResponse>}
         */
        function filter(filterColumns = [], startIndex = 0, stopIndex = 1000) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));

            urlencoded.append("startIndex", startIndex);
            urlencoded.append("stopIndex", stopIndex);

            urlencoded.append("scope", scope);
            urlencoded.append("columns", JSON.stringify(columns));
            return fetchProxy("erp/table/withfilter", urlencoded)
        }

        function removeByFilter(filterColumns = []) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/removerowbyfilter", urlencoded)
        }

        /**
         * This allows to remove a row by a value and a key
         * @param {string | 'indexKey'} key - The name of the column
         * @param {string} value - The Value to look for
         * @param {('is' | 'in' | 'isnot' | 'isexact')} [type='is'] - The type for the filter z.b is or isnot
         * @return {Promise<FetchResponse>}
         */
        function simpleRemove(key, value, type = "is") {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify([{
                column: key, tableName: tableName, type: type, value: value
            }]));

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/removerowbyfilter", urlencoded)

        }


        /**
         *
         * @param filterColumns
         * @param startIndex
         * @param stopIndex
         * @param searchInput
         * @param searchColumns
         * @param searchOffset
         * @param historyTimestamp
         * @returns {Promise<* | {environment: *, data: *, isRead: boolean, id: *|string, type: string, timestamp: *|number}>}
         */
        function search(filterColumns = [], startIndex = 0, stopIndex = 1000, searchInput = "", searchColumns = [], searchOffset = 0, historyTimestamp = 0) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));

            urlencoded.append("startIndex", startIndex);
            urlencoded.append("stopIndex", stopIndex);

            urlencoded.append("scope", scope);
            urlencoded.append("columns", JSON.stringify(columns));


            urlencoded.append("searchInput", searchInput);
            urlencoded.append("searchColumns", JSON.stringify(searchColumns));
            urlencoded.append("searchOffset", searchOffset);

            urlencoded.append("historyTimestamp", historyTimestamp);
            return fetchProxy("erp/table/search", urlencoded)
        }

        /**
         * Gives back the tables' absolute values witch is filtered by the columns defined in {@link filterColumns}
         * @param {Object[]} filterColumns - The Filter Columns Array
         * @param {string} filterColumns[].column - The name of the Column.
         * @param {string} filterColumns[].tableName - The Table Name of the column
         * @param {('is' | 'in'| 'notin' | 'isnot' | 'isexact')} filterColumns[].type - The type of the filter
         * @param {string} filterColumns[].value - The name of the Column.
         * @return {Promise<FetchResponse>}
         */
        function getValues(filterColumns = []) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/getvalues", urlencoded)
        }


        /**
         * The function resolves a hierarchie in a table {@link filterColumns}
         * @param {Object[]} filterColumns - The Filter Columns Array to get the first element from witch the chilled are looked up
         * @param {string} filterColumns[].column - The name of the Column.
         * @param {string} filterColumns[].tableName - The Table Name of the column
         * @param {('is' | 'in'| 'notin' | 'isnot' | 'isexact')} filterColumns[].type - The type of the filter (is | in| notin | isnot | isexact)
         * @param {string} filterColumns[].value - The name of the Column.
         * @param {string} childColumn - The column name of the child column
         * @param {string} parentColumn - THe column name of the parent column
         * @param {number} maxSearchDepth - A value on how deep ( how many level ) the lookup should go ( default => -1 infinitive )
         * @return {Promise<FetchResponse>}
         */
        function recursiveFilter(filterColumns = [], childColumn, parentColumn = 'indexKey', maxSearchDepth = -1) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));
            urlencoded.append("childcolumn", childColumn);
            urlencoded.append("parentcolumn", parentColumn);
            urlencoded.append("maxsearchdepth", maxSearchDepth);

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/recursivewithfilter", urlencoded)
        }

        function getHierarchie(filterColumns = [], allowedTwins = []) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));
            urlencoded.append("allowedTwins", JSON.stringify(allowedTwins));

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/getHierarchie", urlencoded)
        }

        function getRelations() {
            let urlencoded = new URLSearchParams();

            urlencoded.append("tablename", tableName);


            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/getRelations", urlencoded)
        }

        /**
         * Gives back the tables Data
         * @return {Promise<FetchResponse>}
         **/
        function getData() {
            let urlencoded = new URLSearchParams();
            urlencoded.append("tablename", tableName);

            urlencoded.append("scope", scope);
            urlencoded.append("columns", JSON.stringify(columns));
            return fetchProxy("erp/table/getdata", urlencoded)
        }

        /**
         * Gives back the tables Data
         * @return {Promise<FetchResponse>}
         **/
        function getDataAtTimestamp(timestamp) {
            let urlencoded = new URLSearchParams();
            urlencoded.append("tablename", tableName);
            urlencoded.append("timestamp", timestamp);

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/getDataAtTimestamp", urlencoded)
        }

        /**
         * Inserts a row set of values
         * @param {string[]|object} columns - A Array the columns stating with the key column. or a JSON with the data
         * @param {string[]} values - A Array with the values matching the columns.
         * @since 1.0.1
         * @return {Promise<FetchResponse>}
         */
        function insert(columns, values) {
            let urlencoded = new URLSearchParams();
            urlencoded.append("tablename", tableName);
            if (Array.isArray(columns)) {
                urlencoded.append("columnames", JSON.stringify(columns));
                urlencoded.append("values", JSON.stringify(values));
            } else {
                urlencoded.append("columnames", JSON.stringify(Object.keys(columns)));
                urlencoded.append("values", JSON.stringify(Object.values(columns)));
            }
            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/insert", urlencoded)
        }

        /**
         * Updates a row set of values
         * @param {string[]} columnames - A Array the columns stating with the key column.
         * @param {string[]} values - A Array with the values matching the columns.
         * @since 1.0.1
         * @return {Promise<FetchResponse>}
         */
        function update(columnames, values) {
            let urlencoded = new URLSearchParams();
            urlencoded.append("tablename", tableName);
            urlencoded.append("columnames", JSON.stringify(columnames));
            urlencoded.append("values", JSON.stringify(values));

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/update", urlencoded)
        }


        /**
         * Inserts or updates data
         * @param {string[]} columns - The name of the Table.
         * @param {string[]} values - The Filter Columns Array
         * @return {Promise<FetchResponse>}
         */
        function insertOrUpdateRow(columns, values) {
            let urlencoded = new URLSearchParams();
            urlencoded.append("tablename", tableName);
            urlencoded.append("columnames", JSON.stringify(columns));
            urlencoded.append("values", JSON.stringify(values));

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/insertorupdate", urlencoded)
        }


        /**
         * Deletes a row by the value of its indexKey
         * @param {string[]} value - A Array with the values matching the columns.
         * @since 1.0.1
         * @return {Promise<FetchResponse>}
         */
        function deleteByKey(value) {
            let urlencoded = new URLSearchParams();
            urlencoded.append("tablename", tableName);
            urlencoded.append("value", value);

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/removerowbykey", urlencoded, 'POST')

        }

        /**
         *
         * @param columName
         * @param limit
         * @return {Promise<* | {environment: "development" | "production" | "test", data: *, isRead: boolean, id: *|string, type: string, timestamp: number}>}
         */
        function getValueDistinct(columName, limit = 30) {
            let urlencoded = new URLSearchParams();
            urlencoded.append("tablename", tableName);
            urlencoded.append("columname", columName);
            urlencoded.append("limit", limit);

            urlencoded.append("scope", scope);
            return fetchProxy("erp/table/valuedistinct", urlencoded, 'POST')

        }


        /**
         * Gives back the columns of a twin
         * @since 1.0.1
         * @return {Promise<FetchResponse>}
         */
        function structure() {


        }

        /**
         *
         * @param callback
         * @param filter
         * @returns {{subId: *, cancel: function(): void}}
         */
        function onChange(callback, filter = []) {
            let subscriptionName = tableName + "::" + JSON.stringify(filter)

            if (!currentSubscriptions.includes(subscriptionName)) {
                currentSubscriptions.push(subscriptionName)
                Socket.userBus({
                    type: "requestStream",
                    tableName: tableName,
                    duration: 30000,
                    filter: filter
                })
            }


            let subscription = Socket.subscribeToUserBus((data) => {
                if (data.type === "requestStream") {
                    if (data.tableName === tableName) {
                        if (Util.objects.areEqual(data.filter, filter)) {
                            callback(data)
                        }
                    }
                }
            })

            return subscription
        }

        return {
            filter: filter,
            search: search,
            simpleFilter: simpleFilter,
            getRelations: getRelations,
            getHierarchie: getHierarchie,
            getByKey: getByKey,
            deleteByKey: deleteByKey,
            update: update,
            structure: structure,
            getValueDistinct: getValueDistinct,
            getColumns: structure,
            getData: getData,
            getDataAtTimestamp: getDataAtTimestamp,
            insertOrUpdateRow: insertOrUpdateRow,
            insert: insert,
            recursiveFilter: recursiveFilter,
            onChange: onChange,
            getValues: getValues,
            removeByFilter: removeByFilter,
            simpleRemove: simpleRemove,
        }

    }


    function twin(twinId) {
        // check if the twinId is a uuid v4
        if (!(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(twinId))) {
            Notification.createError("The Database Twin only allows for uuids ")
        }

        /**
         * This allows to get a row by a value and a key
         * @param {string | 'indexKey'} key - The name of the column
         * @param {string} value - The Value to look for
         * @param {('is' | 'in' | 'isnot' | 'isexact')} [type='is'] - The type for the filter z.b is or isnot
         * @return {Promise<FetchResponse>}
         */
        function simpleFilter(key, value, type = "is") {
            let urlencoded = new URLSearchParams();

            urlencoded.append("twinid", twinId);
            urlencoded.append("filtercolumns", JSON.stringify([{
                column: key, type: type, value: value
            }]));

            return fetchProxy(("erp/twin/simplefilter"), urlencoded)

        }

        /**
         * Get a record by its indexKey value
         * @param {string} value - The value of the indexKey
         */
        function getByKey(value) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("twinid", twinId);
            urlencoded.append("filtercolumns", JSON.stringify([{
                column: 'indexKey', type: 'isexact', value: value
            }]));
            // check if the value is a uuid v4
            if (!(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value))) {
                Notification.createError("GetByKey only gets a uuid as input ", 'The input is "' + value + "' witch does not match the schema of a uuid v4",)
            }
            return fetchProxy("erp/twin/filter", urlencoded)

        }

        /**
         * Gives back the table witch is filtered by the columns defined in {@link filterColumns}
         * @param {Object[]} filterColumns - The Filter Columns Array
         * @param {string} filterColumns[].column - The name of the Column.
         * @param {string} filterColumns[].tableName - The Table Name of the column
         * @param {('is' | 'in'| 'notin' | 'isnot' | 'isexact')} filterColumns[].type - The type of the filter
         * @param {string} filterColumns[].value - The name of the Column.
         * @return {Promise<FetchResponse>}
         */
        function filter(filterColumns = []) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("twinid", twinId);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));
            return fetchProxy("erp/twin/filter", urlencoded)
        }

        /**
         * Gives back the tables' absolute values witch is filtered by the columns defined in {@link filterColumns}
         * @param {Object[]} filterColumns - The Filter Columns Array
         * @param {string} filterColumns[].column - The name of the Column.
         * @param {string} filterColumns[].tableName - The Table Name of the column
         * @param {('is' | 'in'| 'notin' | 'isnot' | 'isexact')} filterColumns[].type - The type of the filter
         * @param {string} filterColumns[].value - The name of the Column.
         * @return {Promise<FetchResponse>}
         */
        function getValues(filterColumns = []) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("twinid", twinId);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));
            return fetchProxy("erp/twin/getvalues", urlencoded)
        }


        /**
         * The function resolves a hierarchie in a table {@link filterColumns}
         * @param {Object[]} filterColumns - The Filter Columns Array to get the first element from witch the chilled are looked up
         * @param {string} filterColumns[].column - The name of the Column.
         * @param {string} filterColumns[].tableName - The Table Name of the column
         * @param {('is' | 'in'| 'notin' | 'isnot' | 'isexact')} filterColumns[].type - The type of the filter (is | in| notin | isnot | isexact)
         * @param {string} filterColumns[].value - The name of the Column.
         * @param {string} childColumn - The column name of the child column
         * @param {string} parentColumn - THe column name of the parent column
         * @param {number} maxSearchDepth - A value on how deep ( how many level ) the lookup should go ( default => -1 infinitive )
         * @return {Promise<FetchResponse>}
         */
        function recursiveFilter(filterColumns = [], childColumn, parentColumn = 'indexKey', maxSearchDepth = -1) {
            let urlencoded = new URLSearchParams();

            urlencoded.append("twinid", twinId);
            urlencoded.append("filtercolumns", JSON.stringify(filterColumns));
            urlencoded.append("childcolumn", childColumn);
            urlencoded.append("parentcolumn", parentColumn);
            urlencoded.append("maxsearchdepth", maxSearchDepth);
            return fetchProxy("erp/twin/recursivefilter", urlencoded)
        }

        /**
         * Gives back the tables Data
         * @return {Promise<FetchResponse>}
         **/
        function getData() {
            let urlencoded = new URLSearchParams();
            urlencoded.append("twinid", twinId);
            return fetchProxy("erp/twin/filter", urlencoded)
        }

        // /**
        //  * Inserts a row set of values
        //  * @param {string[]|object} columns - A Array the the columns stating with the key column. or a JSON with the data
        //  * @param {string[]} values - A Array with the values matching the columns.
        //  * @since 1.0.1
        //  * @return {Promise<FetchResponse>}
        //  */
        // function insert(columns, values) {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //     if (Array.isArray(columns)) {
        //         urlencoded.append("columnames", JSON.stringify(columns));
        //         urlencoded.append("values", JSON.stringify(values));
        //     } else {
        //         urlencoded.append("columnames", JSON.stringify(Object.keys(columns)));
        //         urlencoded.append("values", JSON.stringify(Object.values(columns)));
        //     }
        //     return fetchProxy((isAdminTable) ? ("erp/insertrowadmin") : ("erp/insertrow"), urlencoded)
        // }
        //
        // /**
        //  * Updates a row set of values
        //  * @param {string[]} columnames - A Array the the columns stating with the key column.
        //  * @param {string[]} values - A Array with the values matching the columns.
        //  * @since 1.0.1
        //  * @return {Promise<FetchResponse>}
        //  */
        // function update(columnames, values) {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //     urlencoded.append("columnames", JSON.stringify(columnames));
        //     urlencoded.append("values", JSON.stringify(values));
        //
        //     return fetchProxy((isAdminTable) ? ("erp/updatevaluesadmin") : ("erp/updatevalues"), urlencoded)
        // }
        //
        // /**
        //  * Inserts a Row
        //  * @param {string[]} columns - The name of the Table.
        //  * @param {string[]} values - The Filter Columns Array
        //  * @return {Promise<FetchResponse>}
        //  */
        // function insertRow(columns, values) {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //     urlencoded.append("columnames", JSON.stringify(columns));
        //     urlencoded.append("values", JSON.stringify(values));
        //     return fetchProxy((isAdminTable) ? ("erp/insertorupdatevaluesadmin") : ("erp/insertorupdatevalues"), urlencoded)
        // }
        //
        // /**
        //  * Inserts or updates data
        //  * @param {string[]} columns - The name of the Table.
        //  * @param {string[]} values - The Filter Columns Array
        //  * @return {Promise<FetchResponse>}
        //  */
        // function insertOrUpdateRow(columns, values) {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //     urlencoded.append("columnames", JSON.stringify(columns));
        //     urlencoded.append("values", JSON.stringify(values));
        //
        //     return fetchProxy((isAdminTable) ? ("erp/insertorupdatevaluesadmin") : ("erp/insertorupdatevalues"), urlencoded)
        // }
        //
        //
        // /**
        //  * Delete a row by the column name and a value
        //  * @param {string[]} columnName - A Array the the columns stating with the key column.
        //  * @param {string[]} value - A Array with the values matching the columns.
        //  * @since 1.0.1
        //  * @return {Promise<FetchResponse>}
        //  */
        // function deleteData(columnName, value) {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //     urlencoded.append("column", columnName);
        //     urlencoded.append("value", value);
        //
        //     return fetchProxy("erp/removerow", urlencoded, 'POST')
        //
        // }
        //
        // /**
        //  * Deletes a row by a value and chooses automatically the key column witch is the first column of the table
        //  * @param {string[]} value - A Array with the values matching the columns.
        //  * @since 1.0.1
        //  * @return {Promise<FetchResponse>}
        //  */
        // function deleteByKey(value) {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //     urlencoded.append("value", value);
        //
        //     return fetchProxy((isAdminTable) ? ("erp/removerowbykeyadmin") : ("erp/removerowbykey"), urlencoded, 'POST')
        //
        // }
        //
        // /**
        //  * Deletes a row by the value of its indexKey
        //  * @param {string[]} value - The value of the indexKey
        //  * @since 1.0.1
        //  * @return {Promise<FetchResponse>}
        //  */
        // function deleteByIndexKey(value) {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //     urlencoded.append("value", value);
        //
        //     return fetchProxy((isAdminTable) ? ("erp/deletebyindexkeyadmin") : ("erp/deletebyindexkey"), urlencoded, 'POST')
        //
        // }
        //
        // /**
        //  * Gives back the columns of a table
        //  * @since 1.0.1
        //  * @return {Promise<FetchResponse>}
        //  */
        // function structure() {
        //     let urlencoded = new URLSearchParams();
        //     urlencoded.append("twinid", twinId);
        //
        //     return fetchProxy("erp/getcolumesbytable", urlencoded)
        //
        // }

        return {
            filter: filter, simpleFilter: simpleFilter, getByKey: getByKey, // deleteData: deleteData,
            // deleteByKey: deleteByKey,
            // deleteByIndexKey: deleteByIndexKey,
            // update: update,
            // structure: structure,
            // getColumns: structure,
            // insertRow: insertRow,
            getData: getData, // insertOrUpdateRow: insertOrUpdateRow,
            // insert: insert,
            recursiveFilter: recursiveFilter, getValues: getValues,
        }

    }


    // allows for all tables but is slower
    return {table: table, twin: twin}
}()

/**
 * Liberty to interact with the File system of the system.
 */
const FileSystem = function () {
    /**
     * List the Files in the FileSystem by the current {@link currentPath}
     * @param {string} currentPath - the path from which to list the files
     * @example FileSystem.list("/myFile.png",{})
     * @return - the list of files
     */
    function list(currentPath) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("currentPath", currentPath);
        return fetchProxy("erp/fileSystem/list", urlencoded)
    }

    /**
     * Put a file in storage bucket specified to configure method
     * @param {string} key - key of the object as base64
     * @param {Object} object - File to be put in bucket
     * @param {Object} [config] - { level : private|protected|public, contentType: MIME Types,
     *  progressCallback: function }
     * @example FileSystem.put("/myFile.png","base64...",{})
     * @return - promise resolves to object on success
     */
    function put(key, object, config) {
        if (object === '') {
            let urlencoded = new URLSearchParams();
            urlencoded.append("key", key);
            urlencoded.append("object", 'data:image/png;base64,');
            urlencoded.append("config", config);
            return fetchProxy("erp/fileSystem/put", urlencoded)

        } else if (typeof object === "object" && object.size < 100000000) {
            return new Promise((resolve) => {
                let reader = new FileReader();
                reader.onload = (event) => {
                    let urlencoded = new URLSearchParams();

                    urlencoded.append("key", key);
                    urlencoded.append("object", event.target.result);
                    urlencoded.append("config", config);
                    resolve(fetchProxy("erp/fileSystem/put", urlencoded))
                }

                reader.readAsDataURL(object);
            });
        } else if (typeof object === "string" && object.length < 100000000 && !object.includes("base64")) {
            // save string
            let urlencoded = new URLSearchParams();

            urlencoded.append("key", key);
            urlencoded.append("object", "base64," + new Buffer.from(object).toString("base64"));
            urlencoded.append("config", config);
            return (fetchProxy("erp/fileSystem/put", urlencoded))

        } else if (typeof object === "string" && object.length < 100000000 && object.includes("base64")) {
            // save base64
            let urlencoded = new URLSearchParams();

            urlencoded.append("key", key);
            urlencoded.append("object", object);
            urlencoded.append("config", config);
            return (fetchProxy("erp/fileSystem/put", urlencoded))

        } else {
            store.dispatch(triggerError({
                title: 'Upload Failed',
                text: 'The File witch should be uploaded is lager than the limit of 100MiB',
                linkShowMe: <div/>,
                errorSourcePath: "",
            }))

            return new Promise((resolve, reject) => {
                reject("To lage payload")
            })
        }


    }

    /**
     * Gets a file by its key
     * @param {string} key - key of the object
     * @param {Object} [config] - { level : private|protected|public, contentType: MIME Types,
     *  progressCallback: function }
     * @example FileSystem.get("/myFile.png",{})
     * @return - it returns the file
     */
    function get(key, config) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("key", key);
        urlencoded.append("config", config);
        return fetchProxy("erp/fileSystem/get", urlencoded, "POST")
    }

    /**
     * Gets a image by its key
     * @param {string} key - key of the objecs
     * @param {number} width
     * @param {Object} [config] - { level : private|protected|public, contentType: MIME Types,
     *  progressCallback: function }
     * @example FileSystem.get("/myFile.png",{})
     * @return - it returns the file
     */
    function getImage(key, width = 1000, config) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("key", key);
        urlencoded.append("width", width);
        urlencoded.append("config", config);
        return fetchProxy("erp/fileSystem/getImage", urlencoded, "POST", "BLOB")
    }

    /**
     * Gets a files metadata without returning the object itself
     * @param {string} key - key of the object
     * @param {Object} [config] - { level : private|protected|public, contentType: MIME Types,
     *  progressCallback: function }
     * @example FileSystem.get("/myFile.png",{})
     * @return - it returns the files metadata
     */
    function getMetadata(key, config) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("key", key);
        urlencoded.append("config", config);
        return fetchProxy("erp/fileSystem/getmetadata", urlencoded)
    }

    /**
     * Download  a file by its key
     * @param {string} key - key of the object
     * @param {Object} [config] - { level : private|protected|public, contentType: MIME Types,
     *  progressCallback: function }
     * @example FileSystem.get("/myFile.png",{})
     * @return - it returns the file
     */
    function download(key, config) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("key", key);
        urlencoded.append("config", config);
        return new Promise(resolve => fetchProxy("erp/fileSystem/get", urlencoded).then(result => {
            if (result?.Body !== undefined) {
                let base64 = new Buffer.from(result.Body).toString('base64');
                Util.files.fileDownloadInBrowser(result.key, base64)
            } else {
                Notification.createError("The file download failed.")
            }
            resolve(result)
        }))
    }

    /**
     * Removes a file by its key
     * @param {string} key - key of the object
     * @param {Object} [config] - { level : private|protected|public, contentType: MIME Types,
     *  progressCallback: function }
     * @example FileSystem.remove("/myFile.png",{})
     * @return {Promise}
     */
    function remove(key, config) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("key", key);
        urlencoded.append("config", config);
        return fetchProxy("erp/fileSystem/remove", urlencoded)
    }

    /**
     * Creates a new Directory at path
     * @example FileSystem.createDir("/dir1/dir2",true)
     * @return {Promise}
     * @param dirPath {string} - The directory path
     * @param recursive {boolean} - Create recursive directory's
     */
    function createDir(dirPath, recursive) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("dirpath", dirPath);
        urlencoded.append("recursive", recursive);
        return fetchProxy("erp/fileSystem/createdir", urlencoded)
    }

    /**
     * Rename a FileSystem Object
     * @example FileSystem.rename("test.png","test2.png")
     * @return {Promise}
     * @param dirPath {string} - The directory path
     * @param recursive {boolean} - Create recursive directory's
     */
    function rename(key, newName) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("key", key);
        urlencoded.append("newName", newName);
        return fetchProxy("erp/fileSystem/rename", urlencoded)
    }

    function getDuckProfile() {
        let urlencoded = new URLSearchParams();

        return fetchProxy("erp/fileSystem/getDuckProfile", urlencoded)
    }


    return {
        list: list,
        getImage: getImage,
        put: put,
        get: get,
        getDuckProfile: getDuckProfile,
        download: download,
        getMetadata: getMetadata,
        remove: remove,
        createDir: createDir,
        rename: rename,
    }
}();


const Deployment = function () {

    function fetch(repoString, githubToken) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("repoString", repoString);
        urlencoded.append("githubToken", githubToken);
        return fetchProxy("code/fetch", urlencoded)
    }

    function deploy(appName, deploymentId, properties) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("appName", appName);
        urlencoded.append("deploymentId", deploymentId);
        urlencoded.append("properties", properties);
        return fetchProxy("code/deploy", urlencoded)
    }


    return {
        fetch: fetch, deploy: deploy,
    }
}();


const Logs = function () {
    function getNodeLogs(nodeId, logeName) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("nodeId", nodeId);
        urlencoded.append("logeName", logeName);
        return fetchProxy("logs/getNodeLogs", urlencoded, "POST")
    }

    function getBuildLogs(buildId) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("buildId", buildId);
        return fetchProxy("logs/getBuildLogs", urlencoded, "POST")
    }

    function getDeploymentLogs(deploymentId) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("deploymentId", deploymentId);
        return fetchProxy("logs/getDeploymentLogs", urlencoded, "POST")
    }

    function getContainerLogs(containerId, logeName) {
        let urlencoded = new URLSearchParams();
        urlencoded.append("containerId", containerId);
        urlencoded.append("logeName", logeName);
        return fetchProxy("logs/getContainerLogs", urlencoded, "POST")
    }

    return {
        getNodeLogs: getNodeLogs,
        getContainerLogs: getContainerLogs,
        getBuildLogs: getBuildLogs,
        getDeploymentLogs: getDeploymentLogs
    }

}()


export const ApiTokenAPI = function (tokenId) {

    function create(newTokenValue, tokenName) {
        let parameter = {}
        parameter.tokenId = tokenId;
        parameter.newTokenValue = newTokenValue;
        parameter.tokenName = tokenName;
        return fetchProxy("ApiToken/create", parameter)
    }

    function validate(token) {
        let parameter = {}
        parameter.tokenId = tokenId;
        parameter.token = token;
        return fetchProxy("ApiToken/validate", parameter)
    }

    function doesExist() {
        let parameter = {}
        parameter.tokenId = tokenId;
        return fetchProxy("ApiToken/doesExist", parameter)
    }

    function getInfo() {
        let parameter = {}
        parameter.tokenId = tokenId;
        return fetchProxy("ApiToken/getInfo", parameter)
    }

    function remove() {
        let parameter = {}
        parameter.tokenId = tokenId;
        return fetchProxy("ApiToken/remove", parameter)
    }

    function update() {
        let parameter = {}
        parameter.tokenId = tokenId;
        return fetchProxy("ApiToken/update", parameter)
    }

    function listAll() {
        let parameter = {}
        parameter.tokenId = tokenId;
        return fetchProxy("ApiToken/listAll", parameter)
    }


    return {
        create,
        validate,
        doesExist,
        getInfo,
        remove,
        update,
        listAll
    }
};

export async function fetchProxy(endpointUrl, urlencoded, method = "POST", responseType = "JSON") {


    let authToken = await CurrentUser.getAuthToken()

    let headers = Fetch.getHeaderAuth(authToken);


    const queryString = (method === "GET") ? ("?" + urlencoded.toString()) : ("")


    let requestOptions = (method === "POST") ? ({
        method: method, headers: headers, body: urlencoded instanceof URLSearchParams
            ? JSON.stringify(Object.fromEntries(urlencoded))
            : JSON.stringify(urlencoded),
        redirect: 'follow'
    }) : ({
        method: method, headers: headers, redirect: 'follow'
    });

    // if the payload is bigger than 2000 bit than compress it
    if (requestOptions.body !== undefined) {
        let bodyString = (requestOptions.body)

        if (bodyString.length > 2000) {
            requestOptions.body = await WebWorker.gzip(bodyString)
            headers.append("Content-Encoding", "gzip");
        }
    }


    return Fetch.retry(() => fetch(import.meta.env.VITE_APP_API_ENDPOINT + endpointUrl + (queryString.length > 1 ? (queryString) : ""), requestOptions), 4)

        .then((response) => {
            switch (responseType) {
                case "BLOB":
                    return response.blob()
                case "JSON":
                default:
                    return response.json()
            }

        })
        .then(data => {
            return data
        })
        .catch((error) => {
            store.dispatch(triggerError({
                title: "Fetch Error",
                text: 'Endpoint Path: ' + endpointUrl + ' ' + error,
                linkShowMe: <div><a href={import.meta.env.VITE_APP_API_ENDPOINT + endpointUrl} target={"_blank"}
                                    rel="noopener noreferrer"> Show me the api
                    results</a></div>,
                errorSourcePath: "",
            }))
            return {recordset: [], error: true}
        });
}


const callWSFunction = async function (functionName, functionInput) {

    let requestId = Unique.newInternalId()
    return new Promise(async (resolve) => {

        Socket.emit(requestId, {
            type: "func", requestId: requestId, functionName: functionName, input: functionInput
        })

        let gotResponce = false
        Socket.subscribeOnce(requestId, (data) => {
            if (data.type === "func" && data.requestId === requestId) {
                gotResponce = true
                if (!Strings.isEmpty(data.data.success) && data.data.success === false) {
                    Notification.createError(`${functionName} failed`, data?.data?.error ?? "No error message")
                }
                resolve(data.data)
            }
        })

        setTimeout(() => {
            if (!gotResponce) {
                console.log(requestId);

            }

        }, 5000)


    })


}

const checkAPIResult = function (data, path) {
    if (data.hasOwnProperty("code") || data.type === "ERROR" || data?.error === true) {
        store.dispatch(triggerError({
            title: data?.originalError?.info?.message,
            text: 'Endpoint Path: ' + path,
            linkShowMe: <div><a href={import.meta.env.VITE_APP_API_ENDPOINT + path} target={"_blank"}
                                rel="noopener noreferrer"> Show me the api
                results</a></div>,
            errorSourcePath: "",
        }))
        return {recordset: [], error: true}
    } else if (data.hasOwnProperty("success")) {
        if (data.success === false) {
            store.dispatch(triggerError({
                title: data.errorMessage, text: 'Endpoint Path: ' + path, linkShowMe: <div/>, errorSourcePath: "",
            }))
            return {}
        } else {
            return data
        }
    } else {
        return data
    }
}

export {
    Database,
    FileSystem,
    Deployment,
    Logs,
    callWSFunction
}